import { MutableRefObject, useState } from 'react';
import { useDispatch } from 'react-redux';
import { API_REQUEST } from '../../../core/actions';
import * as AssetsAPI from '../../../core/api/assets';
import { ArchiveTarget } from '../components/ArchiveConfirmModal';
import {
  deselectSidebarItem,
  deselectTemplate,
  fetchAssetGroups,
  fetchAssetItems,
  fetchAssetSiteInventories,
  fetchAssetTaskOccurrences,
  fetchAssetTemplateOccurrences,
  fetchAssetTemplates,
} from '../actions';

const countDescendants = (node: any): number => {
  const children = node?.children || [];
  return children.reduce((sum: number, child: any) => sum + 1 + countDescendants(child), 0);
};

const ARCHIVE_API: Record<ArchiveTarget['type'], any> = {
  template: AssetsAPI.archiveAssetTemplate,
  group: AssetsAPI.archiveAssetGroup,
  item: AssetsAPI.archiveAssetItem,
  task: AssetsAPI.archiveTaskOccurrence,
};

/** The tree nodes. Tasks can be archived from a detail section but not restored there. */
type StructuralTarget = ArchiveTarget & { type: 'template' | 'group' | 'item' };

const UNARCHIVE_API = {
  template: AssetsAPI.unarchiveAssetTemplate,
  group: AssetsAPI.unarchiveAssetGroup,
  item: AssetsAPI.unarchiveAssetItem,
};

const toArchiveTarget = (node: any): StructuralTarget | null => {
  const type = node?.type;
  if (type !== 'template' && type !== 'group' && type !== 'item') return null;
  const id = node?.rawId ?? node?.raw?.id;
  if (!id) return null;
  return {
    type,
    id: Number(id),
    name: node?.name || '',
    childCount: countDescendants(node),
  };
};

/**
 * Archive, unarchive and unlink for every node kind the tree can hold.
 */
export function useAssetArchive({
  site,
  selectedOccurrenceId,
  selectedTemplateIdRef,
  selectedSidebarItemRef,
  refreshOpenAssetView,
}: {
  site: any;
  selectedOccurrenceId: number | null;
  selectedTemplateIdRef: MutableRefObject<number | null>;
  selectedSidebarItemRef: MutableRefObject<any>;
  refreshOpenAssetView: () => void;
}) {
  const dispatch = useDispatch();

  const [archiveTarget, setArchiveTarget] = useState<ArchiveTarget | null>(null);
  const [archiveSubmitting, setArchiveSubmitting] = useState(false);
  const [archiveError, setArchiveError] = useState<string | null>(null);

  const refreshAfterArchiveChange = (target: ArchiveTarget) => {
    if (target.type === 'task') {
      dispatch(fetchAssetGroups());
      dispatch(fetchAssetItems());
      dispatch(fetchAssetTaskOccurrences());
      // The open template or deployment holds its own copy of the task rows,
      // so without this the row stays on screen and the delete looks ignored.
      refreshOpenAssetView();
      return;
    }
    if (target.type === 'template') {
      dispatch(fetchAssetTemplates());
      return;
    }
    if (target.type === 'group') {
      dispatch(fetchAssetGroups());
    } else {
      dispatch(fetchAssetItems());
      dispatch(fetchAssetGroups());
    }
    refreshOpenAssetView();
  };

  const clearSelectionIfArchived = (target: ArchiveTarget) => {
    if (target.type === 'task') return;
    if (target.type === 'template') {
      if (selectedTemplateIdRef.current === target.id) dispatch(deselectTemplate());
      return;
    }
    const current = selectedSidebarItemRef.current;
    if (current?.itemType === target.type && current?.id === target.id) {
      dispatch(deselectSidebarItem());
    }
  };

  const handleArchiveNode = (node: any) => {
    const target = toArchiveTarget(node);
    if (target) {
      setArchiveError(null);
      setArchiveTarget(target);
    }
  };

  const handleConfirmArchive = () => {
    const target = archiveTarget;
    if (!target) return;
    setArchiveSubmitting(true);
    setArchiveError(null);
    dispatch({
      type: API_REQUEST,
      callAPI: ARCHIVE_API[target.type],
      data: { id: target.id },
      options: { siteId: site },
      action: 'ARCHIVE_ASSET_NODE',
      voidCallback: () => {
        setArchiveSubmitting(false);
        setArchiveTarget(null);
        clearSelectionIfArchived(target);
        refreshAfterArchiveChange(target);
      },
      errorCallback: (err: any) => {
        setArchiveSubmitting(false);
        setArchiveError(err?.data || err?.message || 'archiveFailed');
      },
    });
  };

  const handleUnarchiveNode = (node: any) => {
    const target = toArchiveTarget(node);
    if (!target) return;
    dispatch({
      type: API_REQUEST,
      callAPI: UNARCHIVE_API[target.type],
      data: { id: target.id },
      options: { siteId: site },
      action: 'UNARCHIVE_ASSET_NODE',
      voidCallback: () => refreshAfterArchiveChange(target),
    });
  };

  // Tasks go through the same confirm as the structural nodes -- removing one
  // takes its history and attachments with it.
  const handleArchiveTask = (taskItem: any) => {
    const rawId =
      taskItem?.raw?.id || (taskItem?.id ? String(taskItem.id).replace('task-', '') : null);
    if (!rawId) return;
    setArchiveError(null);
    setArchiveTarget({
      type: 'task',
      id: Number(rawId),
      name: taskItem?.name || taskItem?.raw?.task?.name || '',
      childCount: 0,
    });
  };

  // A part can hang off a template, a sub-asset, or -- once deployed -- an
  // occurrence of either, so unlinking has to clear the column that actually
  // holds the link rather than assuming assetItemId.
  const handleUnlinkInventory = (inventoryItem: any) => {
    const rawId =
      inventoryItem?.raw?.id ||
      (inventoryItem?.id ? String(inventoryItem.id).replace('inv-', '') : null);
    if (!rawId) return;

    const refreshAfterUnlink = () => {
      dispatch(fetchAssetGroups());
      dispatch(fetchAssetItems());
      dispatch(fetchAssetSiteInventories());
      dispatch(fetchAssetTemplateOccurrences());
      refreshOpenAssetView();
    };

    const sourceKind = inventoryItem?.source?.kind;
    const sourceId = Number(inventoryItem?.source?.id);

    if (selectedOccurrenceId) {
      dispatch({
        type: API_REQUEST,
        callAPI: AssetsAPI.patchInventory,
        data: {
          id: Number(rawId),
          assetTemplateOccurrenceId: null,
          assetGroupOccurrenceId: null,
          assetItemOccurrenceId: null,
        },
        options: { siteId: site },
        action: 'UNLINK_INVENTORY_FROM_OCCURRENCE',
        voidCallback: refreshAfterUnlink,
      });
      return;
    }

    if (sourceKind === 'template' && Number.isFinite(sourceId)) {
      dispatch({
        type: API_REQUEST,
        callAPI: AssetsAPI.unlinkTemplateInventory,
        data: { assetTemplateId: sourceId, inventoryId: Number(rawId) },
        options: { siteId: site },
        action: 'UNLINK_INVENTORY_FROM_TEMPLATE',
        voidCallback: refreshAfterUnlink,
      });
      return;
    }

    dispatch({
      type: API_REQUEST,
      callAPI: AssetsAPI.patchInventory,
      data: { id: Number(rawId), assetItemId: null },
      options: { siteId: site },
      action: 'UNLINK_INVENTORY_FROM_ASSET',
      voidCallback: refreshAfterUnlink,
    });
  };

  const handleCancelArchive = () => {
    setArchiveTarget(null);
    setArchiveError(null);
  };

  return {
    archiveTarget,
    handleCancelArchive,
    archiveSubmitting,
    archiveError,
    handleArchiveNode,
    handleConfirmArchive,
    handleUnarchiveNode,
    handleArchiveTask,
    handleUnlinkInventory,
  };
}
